import { ClientAccount, Shipment, ShipmentStatus, StatusEvent } from '../types/shipment';

const STORAGE_KEY = 'alshiraa_shipments_v1';
const CLIENTS_KEY = 'alshiraa_clients_v1';
const SEED_KEY = 'alshiraa_seed_done_v1';

const now = () => new Date().toISOString();

const statusOrder: ShipmentStatus[] = ['arrived', 'inspection', 'clearance', 'release', 'delivered'];

export const getStatusOrder = () => statusOrder;

const demoClients: ClientAccount[] = [
  {
    email: 'client@demo.com',
    password: 'demo2024',
    name: 'أحمد التاجر',
    company: 'شركة النور للاستيراد',
    clientCode: 'T-0001',
  },
  {
    email: 'info@alshiraa-customs.com',
    password: 'demo2024',
    name: 'عميل الشراع',
    company: 'الشراع',
    clientCode: 'T-0002',
  },
];

const demoShipments = (): Shipment[] => {
  const base = now();
  return [
    {
      id: 'sh-001',
      declarationNumber: 'SY-2026-004821',
      clientEmail: 'client@demo.com',
      clientName: 'أحمد التاجر',
      company: 'شركة النور للاستيراد',
      clientCode: 'T-0001',
      cargoDescription: 'مواد غذائية — معلبات',
      border: 'ميناء اللاذقية',
      weightTons: 12,
      status: 'clearance',
      history: [
        { status: 'arrived', at: base, note: 'وصول الحاوية إلى الميناء' },
        { status: 'inspection', at: base, note: 'تم الكشف الجمركي' },
        { status: 'clearance', at: base, note: 'جاري إتمام التخليص' },
      ],
      updatedAt: base,
    },
    {
      id: 'sh-002',
      declarationNumber: 'SY-2026-004915',
      clientEmail: 'client@demo.com',
      clientName: 'أحمد التاجر',
      company: 'شركة النور للاستيراد',
      clientCode: 'T-0001',
      cargoDescription: 'منسوجات',
      border: 'معبر نصيب',
      weightTons: 8,
      status: 'delivered',
      history: statusOrder.map((status) => ({ status, at: base })),
      updatedAt: base,
    },
  ];
};

const readJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const writeJson = (key: string, value: unknown) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const ensureSeedData = () => {
  if (localStorage.getItem(SEED_KEY)) return;
  writeJson(CLIENTS_KEY, demoClients);
  writeJson(STORAGE_KEY, demoShipments());
  localStorage.setItem(SEED_KEY, '1');
};

export const authenticateClient = (email: string, password: string) => {
  ensureSeedData();
  const clients = readJson<ClientAccount[]>(CLIENTS_KEY, []);
  return clients.find((c) => c.email.toLowerCase() === email.toLowerCase() && c.password === password) ?? null;
};

export const authenticateAdmin = (email: string, password: string) => {
  const adminEmail = 'admin@alshiraa-customs.com';
  const adminPassword = process.env.ADMIN_PORTAL_PASSWORD || 'shiraa-admin-2024';
  if (email.toLowerCase() === adminEmail && password === adminPassword) {
    return { email: adminEmail, name: 'إدارة الشراع' };
  }
  return null;
};

export const getShipmentsForClient = (email: string): Shipment[] => {
  ensureSeedData();
  const all = readJson<Shipment[]>(STORAGE_KEY, []);
  return all
    .filter((s) => s.clientEmail.toLowerCase() === email.toLowerCase())
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
};

export const getAllShipments = (): Shipment[] => {
  ensureSeedData();
  return readJson<Shipment[]>(STORAGE_KEY, []).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
};

export const getShipmentByDeclaration = (declarationNumber: string): Shipment | null => {
  ensureSeedData();
  const all = readJson<Shipment[]>(STORAGE_KEY, []);
  return all.find((s) => s.declarationNumber.toLowerCase() === declarationNumber.toLowerCase()) ?? null;
};

export const updateShipmentStatus = (
  id: string,
  status: ShipmentStatus,
  note?: string
): Shipment | null => {
  ensureSeedData();
  const all = readJson<Shipment[]>(STORAGE_KEY, []);
  const index = all.findIndex((s) => s.id === id);
  if (index === -1) return null;

  const event: StatusEvent = { status, at: now(), note };
  const updated: Shipment = {
    ...all[index],
    status,
    history: [...all[index].history.filter((h) => h.status !== status), event].sort(
      (a, b) => statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status)
    ),
    updatedAt: now(),
  };
  all[index] = updated;
  writeJson(STORAGE_KEY, all);
  return updated;
};

export const createShipment = (input: Omit<Shipment, 'id' | 'history' | 'updatedAt' | 'status'> & { status?: ShipmentStatus }): Shipment => {
  ensureSeedData();
  const all = readJson<Shipment[]>(STORAGE_KEY, []);
  const status = input.status ?? 'arrived';
  const shipment: Shipment = {
    ...input,
    id: `sh-${Date.now()}`,
    status,
    history: [{ status, at: now(), note: 'تم تسجيل البيان' }],
    updatedAt: now(),
  };
  all.push(shipment);
  writeJson(STORAGE_KEY, all);
  return shipment;
};
